import { useEffect, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useGetMe } from '../../api/generated/identity/identity';
import {
  getListScheduleChangeProposalsQueryKey,
  getListScheduleCollisionsQueryKey,
  listScheduleChangeProposals,
  listScheduleCollisions,
  useResolveScheduleCollision,
  useRespondToScheduleChangeProposal,
} from '../../api/generated/scheduling/scheduling';
import type { ScheduleChangeProposal, ScheduleCollision } from '../../api/generated/model';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { Field } from '../../design/primitives/Field';
import { formatInPersonTimezone } from '../../i18n/datetime';
import { SettledListView, settledList } from './settledList';
import { instantFromWallClock } from './wallClock';
import styles from './schedule.module.css';

/**
 * Mudancas de horario: propostas a responder e colisoes a resolver.
 *
 * A colisao era **listada e nao resolvida**, e a proposta nao tinha tela
 * nenhuma. As duas filas moram na mesma pagina porque sao o mesmo assunto visto
 * dos dois lados: uma pede o horario novo a quem tem a aula, a outra o impoe
 * quando duas aulas cairam sobre a mesma hora do mesmo professor.
 *
 * **Nada aqui produz colisao ou proposta.** O backend ainda nao as cria
 * (lacunas A4 e a da Onda 1); a tela nasce antes do produtor, e o teste semeia
 * pelo dublê em vez de fingir que a fila enche sozinha.
 */
/** Teto por pagina pedido ao servidor; o contrato aceita ate 200. */
const PROPOSAL_PAGE_LIMIT = 100;

export function ScheduleChangesPage() {
  const { t } = useTranslation();
  const meQuery = useGetMe();
  const me = meQuery.data;

  /**
   * A FILA INTEIRA, e nao a primeira pagina dela.
   *
   * A chamada era `useListScheduleChangeProposals(undefined)`: sem cursor,
   * nunca, e nada nesta tela pedia a segunda pagina. O `nextCursor` que o
   * servidor passou a devolver (scheduling/service.go, `ListProposals`) nao tinha
   * leitor, e o LIMIT que veio junto com ele deixou de ser inofensivo — o que
   * caisse fora da primeira pagina simplesmente nao existia para ninguem.
   *
   * Nesta fila isso e pior do que em qualquer outra lista do produto: uma
   * proposta some com PRAZO CORRENDO. `respondBy` e o compromisso de aviso
   * antecipado, e o professor, que agora tem fila propria, ve a janela inteira de
   * horario de verao das aulas dele — a lista que mais cresce de uma vez so no
   * modulo. Uma proposta nao respondida ate o prazo e a aula andando de horario
   * sem que a aluna tenha tido como dizer nada.
   *
   * Seguir o cursor ate `nextCursor` nulo, e nao um botao de "carregar mais": a
   * pessoa nao tem como saber que a proposta dela ficou na pagina 2, e uma fila
   * de decisao com prazo nao pode depender de alguem desconfiar.
   */
  const proposalsQuery = useInfiniteQuery({
    // A chave e a mesma sem parametros de `getListScheduleChangeProposalsQueryKey()`:
    // o `limit` dentro dela deixaria qualquer invalidacao futura mirando uma
    // consulta que ninguem observa.
    queryKey: getListScheduleChangeProposalsQueryKey(),
    queryFn: ({ pageParam, signal }) =>
      listScheduleChangeProposals(
        pageParam === undefined
          ? { limit: PROPOSAL_PAGE_LIMIT }
          : { limit: PROPOSAL_PAGE_LIMIT, cursor: pageParam },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!me,
  });

  const { hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } = proposalsQuery;
  useEffect(() => {
    // `isFetchNextPageError` faz parte da condicao de parada, e as outras quatro
    // telas com cursor desta casa (attendance, messaging, payroll, billing) ja o
    // trazem — estas duas aqui ficaram para tras. Sem ele, a pagina que falha no
    // meio deixa `hasNextPage` verdadeiro e devolve `isFetchingNextPage` a
    // falso: o efeito dispara de novo, e a tela entra em tempestade de
    // requisicoes contra o servidor que acabou de recusar, que e o pior momento
    // possivel para insistir.
    if (!hasNextPage || isFetchingNextPage || isFetchNextPageError) return;
    void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

  /**
   * A fila de colisao tem o MESMO cursor, e pelo mesmo motivo.
   *
   * E exclusiva de Super Admin no contrato — pedi-la com outro papel voltaria
   * 403, e um erro na tela por uma secao que nao e daquele papel e ruido —, e por
   * isso ela e a fila de UMA pessoa para a plataforma inteira. Uma
   * rematerializacao de horario de verao produz colisao em lote, e o que nao
   * couber na primeira pagina fica sendo duas aulas sobrepostas na grade de um
   * professor que ninguem vai resolver: as duas acontecem no mesmo horario e uma
   * delas nao tem quem a de. Deixar esta consulta sem cursor uma linha abaixo da
   * que acabou de ganhar um seria a meia correcao de novo.
   */
  const collisionsQuery = useInfiniteQuery({
    queryKey: getListScheduleCollisionsQueryKey(),
    queryFn: ({ pageParam, signal }) =>
      listScheduleCollisions(
        pageParam === undefined
          ? { limit: PROPOSAL_PAGE_LIMIT }
          : { limit: PROPOSAL_PAGE_LIMIT, cursor: pageParam },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: me?.role === 'super_admin',
  });

  const collisionsNext = collisionsQuery.hasNextPage;
  const collisionsFetching = collisionsQuery.isFetchingNextPage;
  const collisionsFailed = collisionsQuery.isFetchNextPageError;
  const fetchMoreCollisions = collisionsQuery.fetchNextPage;
  useEffect(() => {
    // Ver a nota do laco das propostas, acima: mesma condicao de parada, mesma
    // razao. Aqui ela pesa mais, porque a rematerializacao de horario de verao
    // produz colisao em lote e este e justamente o momento em que a fila tem
    // muitas paginas para percorrer.
    if (!collisionsNext || collisionsFetching || collisionsFailed) return;
    void fetchMoreCollisions();
  }, [collisionsNext, collisionsFetching, collisionsFailed, fetchMoreCollisions]);

  if (!me) return null;

  // As duas filas passam pelo MESMO derivador, e nao por ternarios escritos a
  // mao em cada secao. A de propostas tinha a guarda de "so diz vazio depois do
  // cursor"; a de colisoes, quarenta linhas abaixo, nao tinha nenhuma — nem a do
  // cursor nem a do erro — e anunciava "Nenhuma colisao na fila" ao unico papel
  // que alcanca essa tela, tanto no primeiro render de cada visita quanto quando
  // a leitura voltava 500. `settledList` faz esse estado deixar de ser
  // exprimivel, aqui e nas telas que vierem depois.
  const proposals = settledList<ScheduleChangeProposal>({
    isError: proposalsQuery.isError,
    hasNextPage,
    items: proposalsQuery.data?.pages.flatMap((page) => page.items),
  });
  const collisions = settledList<ScheduleCollision>({
    isError: collisionsQuery.isError,
    hasNextPage: collisionsNext,
    items: collisionsQuery.data?.pages.flatMap((page) => page.items),
  });

  return (
    <div className={styles.page}>
      <h1>{t('schedule.changes.title')}</h1>

      <section aria-label={t('schedule.changes.proposals')}>
        <h2>{t('schedule.changes.proposals')}</h2>
        <p className={styles.meta}>{t('schedule.changes.proposalsExplained')}</p>

        <SettledListView
          state={proposals}
          empty={t('schedule.changes.proposalsEmpty')}
          emptyClassName={styles.meta}
        >
          {(items) => (
            <ul className={styles.collisionList}>
              {items.map((proposal) => (
                <ProposalCard
                  key={proposal.id}
                  proposal={proposal}
                  locale={me.locale}
                  timezone={me.timezone}
                  // QUEM RESPONDE E A ALUNA, e so ela. `authorizeProposal`
                  // (scheduling/proposal.go) exige `role == student` E
                  // `personID == studentID` da proposta antes de qualquer outra
                  // conferencia; professor e Super Admin levam 403.
                  canRespond={me.role === 'student'}
                  onAnswered={() => void proposalsQuery.refetch()}
                />
              ))}
            </ul>
          )}
        </SettledListView>
      </section>

      {me.role === 'super_admin' ? (
        <section aria-label={t('schedule.collisionsTitle')}>
          <h2>{t('schedule.collisionsTitle')}</h2>
          <SettledListView
            state={collisions}
            empty={t('schedule.changes.collisionsEmpty')}
            emptyClassName={styles.meta}
          >
            {(items) => (
              <ul className={styles.collisionList}>
                {items.map((collision) => (
                  <CollisionCard
                    key={collision.id}
                    collision={collision}
                    locale={me.locale}
                    timezone={me.timezone}
                    onResolved={() => void collisionsQuery.refetch()}
                  />
                ))}
              </ul>
            )}
          </SettledListView>
        </section>
      ) : null}
    </div>
  );
}

/**
 * Uma proposta de mudanca de horario.
 *
 * O horario atual e o proposto aparecem **no fuso da ancora**, e nao no de quem
 * le: a proposta nasce de um deslocamento de horario de verao, e mostrar o par
 * em outro fuso apagaria justamente a diferenca que a pessoa precisa comparar.
 *
 * Recusar aceita um horario alternativo. Recusa sem contraproposta e um "nao"
 * que devolve o problema a quem propos; com ela, a conversa continua.
 *
 * **Ler e responder sao papeis diferentes.** Os botoes saiam de `state ===
 * 'pending'` sozinho, sem olhar quem estava na tela. Enquanto so a aluna
 * alcancava a fila isso passava despercebido; desde que o recorte por papel
 * passou a entregar ao professor as propostas das aulas DELE — e ao Super Admin
 * todas —, os dois passaram a ver "Aceitar" e "Recusar" numa proposta que
 * `authorizeProposal` recusa com 403, porque a tabela nao tem coluna de professor
 * e quem responde e a dona da aula. Oferecer a acao a quem sempre recebe 403 e
 * pior do que nao oferece-la: ensina que a plataforma esta quebrada.
 *
 * `canRespond` chega pronto de quem ja conferiu o papel, em vez de o cartao ler
 * `me` por conta propria: o cartao nao tem como saber se o `me` que ele leria e o
 * mesmo que recortou a lista.
 */
function ProposalCard({
  proposal,
  locale,
  timezone,
  canRespond,
  onAnswered,
}: {
  readonly proposal: ScheduleChangeProposal;
  readonly locale: string;
  readonly timezone: string;
  readonly canRespond: boolean;
  readonly onAnswered: () => void;
}) {
  const { t } = useTranslation();
  const respond = useRespondToScheduleChangeProposal();
  const [alternative, setAlternative] = useState('');

  const pending = proposal.state === 'pending' && canRespond;

  return (
    // Nomeado pelo horario proposto: e o dado que distingue uma proposta da
    // outra, e nao o estado, que se repete em toda a lista.
    <li
      className={styles.card}
      aria-label={formatInPersonTimezone(proposal.proposedStartsAtUtc, proposal.anchorTimezone, locale)}
    >
      <p>
        <strong>{t(`schedule.changes.proposalState.${proposal.state}`)}</strong>
      </p>
      <p>
        {t('schedule.changes.from', {
          time: formatInPersonTimezone(proposal.currentStartsAtUtc, proposal.anchorTimezone, locale),
        })}
      </p>
      <p>
        {t('schedule.changes.to', {
          time: formatInPersonTimezone(proposal.proposedStartsAtUtc, proposal.anchorTimezone, locale),
        })}
      </p>
      <p className={styles.meta}>
        {t('schedule.changes.respondBy', {
          time: formatInPersonTimezone(proposal.respondBy, timezone, locale),
        })}
      </p>

      {respond.isError ? <Alert tone="error">{t('error.unexpected')}</Alert> : null}

      {pending ? (
        <>
          <Field
            label={t('schedule.changes.alternative')}
            type="datetime-local"
            hint={t('schedule.changes.alternativeHint')}
            value={alternative}
            onChange={(event) => setAlternative(event.target.value)}
          />
          <div className={styles.actions}>
            <Button
              variant="primary"
              loading={respond.isPending}
              onClick={() =>
                respond.mutate(
                  { proposalId: proposal.id, data: { decision: 'accept' } },
                  { onSuccess: onAnswered },
                )
              }
            >
              {t('schedule.changes.accept')}
            </Button>
            <Button
              variant="secondary"
              loading={respond.isPending}
              onClick={() =>
                respond.mutate(
                  {
                    proposalId: proposal.id,
                    data: {
                      decision: 'decline',
                      // No fuso DA ANCORA, que e o mesmo em que o cartao acabou
                      // de exibir o horario atual e o proposto. Com
                      // `new Date('...T10:00')` a contraproposta era lida no
                      // relogio do sistema operacional de quem digita: o cartao
                      // se contradizia por dentro — lia em America/New_York e
                      // escrevia no fuso do navegador.
                      alternativeStartsAtUtc:
                        alternative.length > 0
                          ? instantFromWallClock(alternative, proposal.anchorTimezone)
                          : null,
                    },
                  },
                  { onSuccess: onAnswered },
                )
              }
            >
              {t('schedule.changes.decline')}
            </Button>
          </div>
        </>
      ) : null}
    </li>
  );
}

/**
 * Uma colisao, com a resolucao ao lado.
 *
 * Resolver e **mover uma** das ocorrencias em conflito, e a tela obriga a
 * escolher qual: mover a errada nao desfaz a sobreposicao e ainda desloca a aula
 * de quem nao tinha problema. A justificativa e obrigatoria porque a mudanca
 * chega ao professor e ao aluno como fato consumado.
 */
function CollisionCard({
  collision,
  locale,
  timezone,
  onResolved,
}: {
  readonly collision: ScheduleCollision;
  readonly locale: string;
  readonly timezone: string;
  readonly onResolved: () => void;
}) {
  const { t } = useTranslation();
  const resolve = useResolveScheduleCollision();

  const [occurrenceId, setOccurrenceId] = useState('');
  const [newStartsAt, setNewStartsAt] = useState('');
  const [reason, setReason] = useState('');

  const resolved = collision.resolvedAt !== null;
  const canSubmit =
    occurrenceId.length > 0 && newStartsAt.length > 0 && reason.trim().length > 0 && !resolve.isPending;

  return (
    <li className={styles.card} aria-label={collision.teacher.displayName}>
      <p>{collision.teacher.displayName}</p>
      <p className={styles.meta}>
        {t('schedule.changes.detectedAt', {
          time: formatInPersonTimezone(collision.detectedAt, timezone, locale),
        })}
      </p>
      <p className={styles.meta}>
        {t('schedule.changes.occurrenceCount', { count: collision.occurrenceIds.length })}
      </p>

      {resolved ? (
        <p>
          {t('schedule.changes.resolvedAt', {
            time: formatInPersonTimezone(collision.resolvedAt as string, timezone, locale),
          })}
        </p>
      ) : (
        <>
          {resolve.isError ? <Alert tone="error">{t('error.unexpected')}</Alert> : null}

          <label className={styles.selectLabel}>
            {t('schedule.changes.whichToMove')}
            <select value={occurrenceId} onChange={(event) => setOccurrenceId(event.target.value)}>
              <option value="">{t('schedule.changes.whichPlaceholder')}</option>
              {collision.occurrenceIds.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </select>
          </label>

          <Field
            label={t('schedule.changes.newStartsAt')}
            type="datetime-local"
            required
            value={newStartsAt}
            onChange={(event) => setNewStartsAt(event.target.value)}
          />
          <Field
            label={t('schedule.changes.reason')}
            required
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />

          <div className={styles.actions}>
            <Button
              variant="primary"
              disabled={!canSubmit}
              loading={resolve.isPending}
              onClick={() =>
                resolve.mutate(
                  {
                    collisionId: collision.id,
                    data: {
                      occurrenceId,
                      // No fuso de quem resolve, que e o mesmo em que
                      // `detectedAt` aparece logo acima. **Nao e o fuso ancora
                      // da aula**: `ScheduleCollision` nao o carrega, e inventar
                      // um seria pior que assumir o de quem digita. O dia em que
                      // o contrato trouxer a ocorrencia junto da colisao, este e
                      // o argumento a trocar.
                      newStartsAtUtc: instantFromWallClock(newStartsAt, timezone),
                      reason: reason.trim(),
                    },
                  },
                  { onSuccess: onResolved },
                )
              }
            >
              {t('schedule.changes.resolve')}
            </Button>
          </div>
        </>
      )}
    </li>
  );
}
